import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useSubscriptionStore } from '../store/subscriptionStore';
import { useOrganizationStore } from '../store/organizationStore';
import { useToast } from '../contexts/ToastContext';
import { Team } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import ConfirmationModal from '../components/ConfirmationModal';
import { usePageTitle } from '../hooks/usePageTitle';
import { useJiraStore } from '../store/jiraStore';
import { useUserPermissions } from '../hooks/useUserPermissions';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const ACCENT_GRADIENTS = [
  'from-orange-400 to-orange-600',
  'from-orange-400 to-orange-600',
  'from-emerald-500 to-teal-600',
  'from-rose-500 to-pink-600',
  'from-amber-500 to-orange-500',
  'from-cyan-500 to-sky-600',
  'from-orange-300 to-orange-500',
];

function teamAccent(name: string): string {
  const idx = name.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % ACCENT_GRADIENTS.length;
  return ACCENT_GRADIENTS[idx];
}

// ─── Jira Settings Modal ──────────────────────────────────────────────────────

interface JiraSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  teamName: string;
}

const JiraSettingsModal: React.FC<JiraSettingsModalProps> = ({ isOpen, onClose, teamId, teamName }) => {
  const { showSuccess, showError } = useToast();
  const { integration, loadingIntegration, fetchIntegration, saveIntegration, deleteIntegration, testConnection, reset } = useJiraStore();

  const [form, setForm] = useState({ domain: '', email: '', api_token: '', project_key: '' });
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const load = useCallback(async () => { await fetchIntegration(teamId); }, [fetchIntegration, teamId]);

  useEffect(() => {
    if (isOpen) { load(); } else { reset(); setForm({ domain: '', email: '', api_token: '', project_key: '' }); }
  }, [isOpen, load, reset]);

  useEffect(() => {
    if (integration) setForm(prev => ({ ...prev, domain: integration.domain, email: integration.email, project_key: integration.project_key }));
  }, [integration]);

  const handleTest = async () => {
    setTesting(true);
    try {
      if (!integration) { showError('Save the integration first before testing.'); return; }
      const result = await testConnection(teamId);
      result.success ? showSuccess(result.message) : showError(result.message);
    } finally { setTesting(false); }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload: any = {
        domain: form.domain.replace(/^https?:\/\//, '').replace(/\/$/, ''),
        email: form.email,
        project_key: form.project_key.toUpperCase(),
      };
      if (form.api_token) payload.api_token = form.api_token;
      await saveIntegration(teamId, payload);
      showSuccess('Jira integration saved!');
      await load();
    } catch (e: any) {
      showError(e.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await deleteIntegration(teamId);
      showSuccess('Jira integration removed.');
      setForm({ domain: '', email: '', api_token: '', project_key: '' });
    } catch (e: any) {
      showError(e.message || 'Failed to disconnect');
    } finally { setDisconnecting(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md shadow-2xl">
        <div className="flex items-center gap-3 mb-5">
          <svg className="w-6 h-6 shrink-0" viewBox="0 0 32 32" fill="none">
            <path d="M16 0C7.163 0 0 7.163 0 16s7.163 16 16 16 16-7.163 16-16S24.837 0 16 0z" fill="#2684FF" />
            <path d="M23.647 8.353l-7.646 7.648-3.882-3.882-3.766 3.764 3.882 3.882 3.766 3.764 11.412-11.41-3.766-3.766z" fill="white" />
          </svg>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Jira Integration</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">{teamName}</p>
          </div>
          {integration && (
            <span className="ml-auto flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 px-2 py-1 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Connected
            </span>
          )}
        </div>

        {loadingIntegration ? (
          <div className="flex justify-center py-8"><LoadingSpinner size="medium" /></div>
        ) : (
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Atlassian Domain *</label>
              <input type="text" required placeholder="yourcompany.atlassian.net" className="input-field" value={form.domain} onChange={e => setForm({ ...form, domain: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email *</label>
              <input type="email" required placeholder="you@company.com" className="input-field" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">API Token {integration ? '(leave blank to keep existing)' : '*'}</label>
              <input type="password" required={!integration} placeholder={integration?.has_token ? '••••••••••••' : 'Paste your API token'} className="input-field" value={form.api_token} onChange={e => setForm({ ...form, api_token: e.target.value })} autoComplete="new-password" />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Generate at <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noopener noreferrer" className="underline">Atlassian Account Settings</a>
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Project Key *</label>
              <input type="text" required placeholder="PROJ" className="input-field uppercase" value={form.project_key} onChange={e => setForm({ ...form, project_key: e.target.value.toUpperCase() })} />
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Saving…' : 'Save Integration'}</button>
              {integration && (
                <button type="button" onClick={handleTest} disabled={testing} className="px-3 py-2 text-sm border border-orange-300 text-orange-600 rounded-lg hover:bg-orange-50 transition-colors">
                  {testing ? 'Testing…' : 'Test'}
                </button>
              )}
              <button type="button" onClick={onClose} className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">Cancel</button>
            </div>
            {integration && (
              <button type="button" onClick={handleDisconnect} disabled={disconnecting} className="w-full text-sm text-red-600 dark:text-red-400 hover:underline mt-1">
                {disconnecting ? 'Disconnecting…' : 'Disconnect Jira'}
              </button>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

// ─── Create Team Modal ────────────────────────────────────────────────────────

interface CreateTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; description: string }) => void;
}

const CreateTeamModal: React.FC<CreateTeamModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({ name: '', description: '' });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.name.trim()) { onSubmit(formData); setFormData({ name: '', description: '' }); onClose(); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md shadow-2xl">
        <h2 className="text-xl font-semibold mb-1 text-gray-900 dark:text-white">Create New Team</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">Teams group your boards and members together.</p>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Team Name *</label>
            <input type="text" required className="input-field" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Frontend Team" autoFocus />
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description <span className="text-gray-400 font-normal">(optional)</span></label>
            <textarea className="input-field" rows={3} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Brief description about this team..." />
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button type="submit" className="btn-primary flex-1">Create Team</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Invite Link Modal ────────────────────────────────────────────────────────

interface InviteLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  team: Team | null;
}

const InviteLinkModal: React.FC<InviteLinkModalProps> = ({ isOpen, onClose, team }) => {
  const [copied, setCopied] = useState(false);
  const { showSuccess } = useToast();

  if (!isOpen || !team) return null;

  const inviteUrl = team.invite_url || (team.invite_token && team.invite_token !== 'None' && team.invite_token !== null
    ? `${window.location.origin}/teams/join/${team.invite_token}`
    : null);

  if (!inviteUrl) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-lg text-center">
          <p className="text-gray-600 dark:text-gray-400">Loading invite link...</p>
        </div>
      </div>
    );
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
    } catch {
      const el = document.createElement('textarea');
      el.value = inviteUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    showSuccess('Copied!', 'Invite link copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-lg shadow-2xl">
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Invite to {team.name}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" aria-label="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">Share this link with anyone you want to add to the team.</p>

        <div className="mb-6">
          <div className="flex gap-2">
            <input type="text" value={inviteUrl} readOnly className="input-field flex-1 bg-gray-50 dark:bg-gray-700 text-sm" />
            <button onClick={handleCopy} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${copied ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-orange-500 text-white hover:bg-orange-600'}`}>
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
        </div>

        <div className="text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 font-medium uppercase tracking-wide">Or scan QR code</p>
          <div className="inline-block p-3 bg-white rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <img src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(inviteUrl)}`} alt="QR Code" className="w-44 h-44" />
          </div>
        </div>

        <button onClick={onClose} className="btn-secondary w-full mt-5">Done</button>
      </div>
    </div>
  );
};

// ─── Team Members Modal ───────────────────────────────────────────────────────

interface TeamMembersModalProps {
  isOpen: boolean;
  onClose: () => void;
  team: Team | null;
  onRemoveMember: (teamId: string, userId: string) => void;
  onToggleTeamAdmin: (teamId: string, userId: string, isAdmin: boolean) => void;
  currentUserId: string;
  canManage: boolean;
  isOrgAdmin: boolean;
  subscription?: any;
}

const TeamMembersModal: React.FC<TeamMembersModalProps> = ({
  isOpen, onClose, team, onRemoveMember, onToggleTeamAdmin,
  currentUserId, canManage, isOrgAdmin, subscription,
}) => {
  if (!isOpen || !team) return null;

  const limitInfo = subscription?.team_member_limits?.[team.id];
  const adminIds = new Set((team.admins || []).map((a) => a.id));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md shadow-2xl">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{team.name}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {limitInfo?.limit !== null && limitInfo ? `${limitInfo.current}/${limitInfo.limit} members` : `${team.member_count} members`}
              {limitInfo && !limitInfo.can_add && <span className="text-red-600 font-semibold ml-2">· Limit reached</span>}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" aria-label="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {limitInfo && !limitInfo.can_add && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center justify-between">
            <span className="text-sm text-red-800 dark:text-red-200">Team member limit reached</span>
            <Link to="/app/subscription" onClick={onClose} className="text-xs font-semibold text-red-600 dark:text-red-400 hover:underline">Upgrade →</Link>
          </div>
        )}

        <div className="space-y-2 max-h-96 overflow-y-auto">
          {/* Team creator row */}
          <div className="flex items-center justify-between p-3 bg-orange-50 dark:bg-orange-800/20 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-medium" style={{ backgroundColor: team.created_by.avatar_color }}>
                {team.created_by.initials}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{team.created_by.display_name}</p>
                <p className="text-xs text-orange-500 dark:text-orange-400">Owner</p>
              </div>
            </div>
          </div>

          {/* Member rows */}
          {team.members.map((member) => {
            const isMemberAdmin = adminIds.has(member.id);
            return (
              <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-medium" style={{ backgroundColor: member.avatar_color }}>
                    {member.initials}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{member.display_name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {isMemberAdmin ? (
                        <span className="text-violet-600 dark:text-violet-400 font-medium">Team Admin</span>
                      ) : 'Member'}
                    </p>
                  </div>
                </div>

                {canManage && member.id !== currentUserId && (
                  <div className="flex items-center gap-1">
                    {isOrgAdmin && (
                      <button
                        onClick={() => onToggleTeamAdmin(team.id, member.id, isMemberAdmin)}
                        title={isMemberAdmin ? 'Remove Team Admin' : 'Make Team Admin'}
                        className={`p-1.5 rounded-lg transition-colors text-xs font-medium ${
                          isMemberAdmin
                            ? 'text-violet-600 bg-violet-50 dark:bg-violet-900/20 hover:bg-violet-100 dark:hover:bg-violet-900/40'
                            : 'text-gray-400 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20'
                        }`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      </button>
                    )}
                    <button
                      onClick={() => onRemoveMember(team.id, member.id)}
                      title="Remove member"
                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <button onClick={onClose} className="btn-primary w-full mt-5">Done</button>
      </div>
    </div>
  );
};

// ─── Team Card ────────────────────────────────────────────────────────────────

interface TeamCardProps {
  team: Team;
  currentUserId: string;
  subscription: any;
  canManage: boolean;
  isOrgAdmin: boolean;
  onDeleteClick: (id: string, name: string) => void;
  onViewMembers: (team: Team) => void;
  onInviteLink: (team: Team) => void;
  onJiraClick: (team: Team) => void;
}

const TeamCard: React.FC<TeamCardProps> = ({ team, currentUserId, subscription, canManage, isOrgAdmin, onDeleteClick, onViewMembers, onInviteLink, onJiraClick }) => {
  const navigate = useNavigate();
  const limitInfo = subscription?.team_member_limits?.[team.id];
  const isAtLimit = limitInfo && !limitInfo.can_add;

  const allMembers = [team.created_by, ...team.members];
  const visibleAvatars = allMembers.slice(0, 4);
  const extraCount = allMembers.length - visibleAvatars.length;

  const memberText = limitInfo?.limit != null
    ? `${limitInfo.current}/${limitInfo.limit}`
    : String(team.member_count);

  return (
    <article
      className="group bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden flex flex-col hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
      aria-label={`Team: ${team.name}`}
    >
      {/* Gradient accent */}
      <div className={`h-0.5 bg-gradient-to-r ${teamAccent(team.name)}`} />

      <div className="p-4 flex flex-col flex-1 gap-3.5">

        {/* Header */}
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${teamAccent(team.name)} flex items-center justify-center shrink-0`}>
            <span className="text-white font-bold text-xs">{team.name.slice(0, 2).toUpperCase()}</span>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">{team.name}</h3>
              {team.created_by.id === currentUserId && (
                <span className="shrink-0 text-[10px] text-orange-500 dark:text-orange-400 bg-orange-50 dark:bg-orange-800/30 px-1.5 py-0.5 rounded font-medium">
                  Owner
                </span>
              )}
              {!isOrgAdmin && team.is_team_admin && team.created_by.id !== currentUserId && (
                <span className="shrink-0 text-[10px] text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/30 px-1.5 py-0.5 rounded font-medium">
                  Team Admin
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">
              {team.description || 'No description'}
            </p>
          </div>

          {isOrgAdmin && (
            <button
              onClick={(e) => { e.stopPropagation(); onDeleteClick(team.id, team.name); }}
              className="p-1.5 rounded-lg text-gray-300 dark:text-gray-600 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors opacity-0 group-hover:opacity-100 shrink-0"
              title="Delete team"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>

        {/* Stats + Avatars */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
            <span className={isAtLimit ? 'text-red-500 font-medium' : ''}>
              {memberText} member{team.member_count !== 1 ? 's' : ''}
            </span>
            <span className="text-gray-200 dark:text-gray-700">·</span>
            <span>{team.board_count} board{team.board_count !== 1 ? 's' : ''}</span>
          </div>

          <div className="flex -space-x-1.5">
            {visibleAvatars.map((member, idx) => (
              <div
                key={member.id}
                className="w-5 h-5 rounded-full border-2 border-white dark:border-gray-800 flex items-center justify-center text-white text-[9px] font-medium"
                style={{ backgroundColor: member.avatar_color, zIndex: visibleAvatars.length - idx }}
                title={member.display_name}
              >
                {member.initials}
              </div>
            ))}
            {extraCount > 0 && (
              <div
                className="w-5 h-5 rounded-full border-2 border-white dark:border-gray-800 bg-gray-100 dark:bg-gray-600 flex items-center justify-center text-[9px] font-medium text-gray-500 dark:text-gray-300"
                style={{ zIndex: 0 }}
              >
                +{extraCount}
              </div>
            )}
          </div>
        </div>

        {/* Action row */}
        <div className="flex items-center gap-1 pt-2 border-t border-gray-100 dark:border-gray-700 mt-auto">
          <button
            onClick={() => onViewMembers(team)}
            className="p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            title="Members"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
          </button>

          {canManage && (
            <button
              onClick={() => onInviteLink(team)}
              className="p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
              title="Invite"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
              </svg>
            </button>
          )}

          <button
            onClick={() => onJiraClick(team)}
            className="p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:bg-orange-50 hover:text-orange-600 transition-colors"
            title="Jira"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 32 32" fill="none">
              <path d="M16 0C7.163 0 0 7.163 0 16s7.163 16 16 16 16-7.163 16-16S24.837 0 16 0z" fill="#2684FF" />
              <path d="M23.647 8.353l-7.646 7.648-3.882-3.882-3.766 3.764 3.882 3.882 3.766 3.764 11.412-11.41-3.766-3.766z" fill="white" />
            </svg>
          </button>

          <button
            onClick={() => navigate(`/app/dashboard?team=${team.id}`)}
            className="ml-auto flex items-center gap-1 px-3 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium transition-colors"
          >
            View Boards
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </article>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const TeamsPage: React.FC = () => {
  usePageTitle('Teams');
  const { user } = useAuthStore();
  const { currentSubscription, fetchCurrentSubscription } = useSubscriptionStore();
  const { addTeamAdmin, removeTeamAdmin } = useOrganizationStore();
  const { showSuccess, showError } = useToast();
  const { isOrgAdmin, canManageTeam } = useUserPermissions();

  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [isMembersModalOpen, setIsMembersModalOpen] = useState(false);
  const [isInviteLinkModalOpen, setIsInviteLinkModalOpen] = useState(false);
  const [jiraTeam, setJiraTeam] = useState<Team | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ isOpen: false, teamId: '', teamName: '' });

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const fetchTeams = async () => {
    setLoading(true);
    try {
      const token = useAuthStore.getState().tokens?.access;
      if (!token) throw new Error('No access token');
      const res = await fetch(`${API_BASE_URL}/teams/`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to fetch teams');
      const data = await res.json();
      setTeams(data.results || data);
    } catch {
      showError('Error', 'Failed to fetch teams');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async (teamData: { name: string; description: string }) => {
    try {
      const token = useAuthStore.getState().tokens?.access;
      if (!token) throw new Error('No access token');
      const res = await fetch(`${API_BASE_URL}/teams/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(teamData),
      });
      if (!res.ok) throw new Error('Failed to create team');
      const newTeam = await res.json();
      setTeams(prev => [newTeam, ...prev]);
      await fetchCurrentSubscription();
      showSuccess('Team Created!', `${teamData.name} has been created successfully.`);
    } catch (error: any) {
      showError('Error', error.message || 'Failed to create team');
    }
  };

  const handleDeleteTeam = async (teamId: string) => {
    try {
      const token = useAuthStore.getState().tokens?.access;
      if (!token) throw new Error('No access token');
      const res = await fetch(`${API_BASE_URL}/teams/${teamId}/`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to delete team');
      setTeams(prev => prev.filter(t => t.id !== teamId));
      showSuccess('Team Deleted', 'Team has been deleted successfully');
    } catch {
      showError('Error', 'Failed to delete team');
    }
  };

  const handleRemoveMember = async (teamId: string, userId: string) => {
    try {
      const token = useAuthStore.getState().tokens?.access;
      if (!token) throw new Error('No access token');
      const res = await fetch(`${API_BASE_URL}/teams/${teamId}/remove_member/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: userId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to remove member');
      }
      setTeams(prev => prev.map(t =>
        t.id === teamId
          ? { ...t, members: t.members.filter(m => m.id !== userId), admins: (t.admins || []).filter(a => a.id !== userId) }
          : t
      ));
      if (selectedTeam?.id === teamId) setSelectedTeam(prev => prev ? {
        ...prev,
        members: prev.members.filter(m => m.id !== userId),
        admins: (prev.admins || []).filter(a => a.id !== userId),
      } : null);
      await fetchCurrentSubscription();
      showSuccess('Member Removed', 'Member has been removed from the team');
    } catch (error: any) {
      showError('Error', error.message || 'Failed to remove member');
    }
  };

  const handleToggleTeamAdmin = async (teamId: string, userId: string, isCurrentlyAdmin: boolean) => {
    try {
      if (isCurrentlyAdmin) {
        await removeTeamAdmin(teamId, userId);
        setTeams(prev => prev.map(t =>
          t.id === teamId ? { ...t, admins: (t.admins || []).filter(a => a.id !== userId) } : t
        ));
        if (selectedTeam?.id === teamId) setSelectedTeam(prev =>
          prev ? { ...prev, admins: (prev.admins || []).filter(a => a.id !== userId) } : null
        );
        showSuccess('Team Admin Removed', 'User is no longer a team admin');
      } else {
        await addTeamAdmin(teamId, userId);
        const team = teams.find(t => t.id === teamId);
        const member = team?.members.find(m => m.id === userId);
        if (member) {
          setTeams(prev => prev.map(t =>
            t.id === teamId ? { ...t, admins: [...(t.admins || []), member] } : t
          ));
          if (selectedTeam?.id === teamId) setSelectedTeam(prev =>
            prev ? { ...prev, admins: [...(prev.admins || []), member] } : null
          );
        }
        showSuccess('Team Admin Added', 'User is now a team admin');
      }
    } catch (error: any) {
      showError('Error', error.message || 'Failed to update team admin');
    }
  };

  useEffect(() => {
    fetchTeams();
    fetchCurrentSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCurrentSubscription]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  const totalMembers = teams.reduce((sum, t) => sum + t.member_count, 0);
  const totalBoards = teams.reduce((sum, t) => sum + t.board_count, 0);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Page header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Teams</h1>
            <div className="flex items-center gap-3 mt-1.5">
              <span className="text-sm text-gray-500 dark:text-gray-400">{teams.length} team{teams.length !== 1 ? 's' : ''}</span>
              {teams.length > 0 && (
                <>
                  <span className="text-gray-300 dark:text-gray-600">·</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">{totalMembers} members</span>
                  <span className="text-gray-300 dark:text-gray-600">·</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">{totalBoards} boards</span>
                </>
              )}
            </div>
          </div>

          {isOrgAdmin && (
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="btn-primary flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Team
            </button>
          )}
        </div>

        {/* Limit banner */}
        {(() => {
          const atLimit = teams.filter(t => {
            const li = currentSubscription?.team_member_limits?.[t.id];
            return li && !li.can_add;
          });
          if (atLimit.length === 0) return null;
          return (
            <div className="mb-5 flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
              <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
                {atLimit.length} team{atLimit.length > 1 ? 's' : ''} reached the member limit
              </span>
              <Link to="/app/subscription" className="text-xs font-semibold text-amber-700 dark:text-amber-400 hover:underline">Upgrade plan →</Link>
            </div>
          );
        })()}

        {/* Empty state */}
        {teams.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 rounded-2xl bg-orange-50 dark:bg-orange-800/20 flex items-center justify-center mb-5">
              <svg className="w-8 h-8 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No teams yet</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-xs">
              {isOrgAdmin ? 'Create your first team to start running retrospectives with your crew.' : 'You have not been added to any team yet.'}
            </p>
            {isOrgAdmin && <button onClick={() => setIsCreateModalOpen(true)} className="btn-primary">Create Your First Team</button>}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {teams.map(team => (
              <TeamCard
                key={team.id}
                team={team}
                currentUserId={user?.id || ''}
                subscription={currentSubscription}
                canManage={canManageTeam(team)}
                isOrgAdmin={isOrgAdmin}
                onDeleteClick={(id, name) => setDeleteConfirmation({ isOpen: true, teamId: id, teamName: name })}
                onViewMembers={t => { setSelectedTeam(t); setIsMembersModalOpen(true); }}
                onInviteLink={t => { setSelectedTeam(t); setIsInviteLinkModalOpen(true); }}
                onJiraClick={t => setJiraTeam(t)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateTeamModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onSubmit={handleCreateTeam} />

      <TeamMembersModal
        isOpen={isMembersModalOpen}
        onClose={() => setIsMembersModalOpen(false)}
        team={selectedTeam}
        onRemoveMember={handleRemoveMember}
        onToggleTeamAdmin={handleToggleTeamAdmin}
        currentUserId={user?.id || ''}
        canManage={selectedTeam ? canManageTeam(selectedTeam) : false}
        isOrgAdmin={isOrgAdmin}
        subscription={currentSubscription}
      />

      <InviteLinkModal isOpen={isInviteLinkModalOpen} onClose={() => setIsInviteLinkModalOpen(false)} team={selectedTeam} />

      <ConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        onClose={() => setDeleteConfirmation({ isOpen: false, teamId: '', teamName: '' })}
        onConfirm={() => { handleDeleteTeam(deleteConfirmation.teamId); setDeleteConfirmation({ isOpen: false, teamId: '', teamName: '' }); }}
        title="Delete Team"
        message={`Are you sure you want to delete "${deleteConfirmation.teamName}"? This action cannot be undone and will also delete all boards associated with this team.`}
        confirmText="Delete Team"
        cancelText="Cancel"
        type="danger"
      />

      {jiraTeam && (
        <JiraSettingsModal isOpen={true} onClose={() => setJiraTeam(null)} teamId={jiraTeam.id} teamName={jiraTeam.name} />
      )}
    </div>
  );
};

export default TeamsPage;
